import { useState, useRef, useEffect, useMemo } from "react";
import { FiMoreHorizontal, FiEye, FiEdit, FiTrash2, FiDownload, FiX, FiPlus } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import SummaryApi from "../common/SummaryApi";
import api from "../common/apiClient";
import * as XLSX from "xlsx";
import PageLoader from "../helpers/PageLoader";

const PropertyList = () => {
  const [loading, setLoading] = useState(false);
  const [properties, setProperties] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("All Categories");
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const btnRefs = useRef({});
  const menuRef = useRef(null);
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Fetch properties
  const fetchProperties = async () => {
    try {
      setLoading(true);
      const res = await api({
        url: SummaryApi.getAllProperties.url,
        method: SummaryApi.getAllProperties.method,
      });
      const data = res.data?.data || [];
      setProperties(data);
    } catch (err) {
      console.error("Property fetch error:", err);
      toast.error("Failed to load properties");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProperties();
  }, []);

  // Categories for filter
  const categories = useMemo(() => {
    const unique = [...new Set(properties.map((p) => p.category).filter(Boolean))];
    return unique;
  }, [properties]);

  // Filtering Logic
  const filteredData = useMemo(() => {
    let data = properties;
    if (filter !== "All Categories") {
      data = data.filter((item) => item.category === filter);
    }
    if (search) {
      const term = search.toLowerCase();
      data = data.filter(
        (item) =>
          item.title?.toLowerCase().includes(term) ||
          item.location?.toLowerCase().includes(term) ||
          item.category?.toLowerCase().includes(term)
      );
    }
    return data;
  }, [properties, search, filter]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  
  const currentData = useMemo(() => {
    return filteredData.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredData, startIndex]);

  // EXPORT TO EXCEL
  const exportToExcel = () => {
    const excelData = filteredData.map((item) => ({
      "Title": `${item.title || ""}`,
      "Category": `${item.category || ""}`,
      "Location": `${item.location || ""}`,
      "Price": `${item.pricing || ""}`,
      "Land Size": `${item.landSize || ""}`,
      "Status": `${item.status || "Draft"}`,
      "Created Date": `${new Date(item.createdAt).toLocaleDateString()}`,
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);

    const colWidths = Object.keys(excelData[0] || {}).map((key) => {
      const headerLen = key.length;
      const maxCellLen = excelData.reduce((max, row) => {
        const cellLen = row[key] ? row[key].toString().length : 0;
        return Math.max(max, cellLen);
      }, headerLen);
      return { wch: maxCellLen + 2 };
    });

    worksheet["!cols"] = colWidths;
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Properties");
    XLSX.writeFile(workbook, "Property_List.xlsx");
  };

  // Boundary Aware Toggle
  const handleToggle = (id) => {
    if (openMenuId === id) {
      setOpenMenuId(null);
      return;
    }
    const button = btnRefs.current[id];
    if (!button) return;

    const rect = button.getBoundingClientRect();
    const dropdownHeight = 120; // Approx height for 3 buttons
    const dropdownWidth = 140;
    
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    let topPosition;
    if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
      topPosition = rect.top - dropdownHeight;
    } else {
      topPosition = rect.bottom + 5;
    }

    setMenuPosition({ 
      top: topPosition, 
      left: rect.left - dropdownWidth + rect.width 
    });
    setOpenMenuId(id);
  };

  // Delete Functionality
  const confirmDelete = async () => {
    try {
      const res = await api({
        url: SummaryApi.deleteOneProperty.url(selectedId),
        method: SummaryApi.deleteOneProperty.method,
      });

      if (res.data?.success) {
        toast.success("Property deleted successfully");
        setProperties(prev => prev.filter(p => p._id !== selectedId));
        setShowDeleteModal(false);
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete property");
    }
  };

  useEffect(() => {
    const handleEvents = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) &&
          !Object.values(btnRefs.current).some((btn) => btn?.contains(event.target))) {
        setOpenMenuId(null);
      }
    };
    const handleScrollResize = () => setOpenMenuId(null);

    document.addEventListener("mousedown", handleEvents);
    window.addEventListener("scroll", handleScrollResize, true);
    window.addEventListener("resize", handleScrollResize);
    return () => {
      document.removeEventListener("mousedown", handleEvents);
      window.removeEventListener("scroll", handleScrollResize, true);
      window.removeEventListener("resize", handleScrollResize);
    };
  }, []);

  if (loading) return <PageLoader />;

  return (
    <div>
      {/* Header Section */}
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-2xl pb-4 md:text-3xl font-serif text-gray-800">
          Property List
        </h1>
        <div className="flex gap-2 mb-4">
            <button
            onClick={() => navigate("/admin-panel/property-list/property-form")}
            className="flex items-center gap-2 bg-[#45A301] text-white px-4 py-2 rounded-md text-sm hover:bg-[#3a8a01] transition-all"
            >
            <FiPlus size={16} />
            <span>Add Property</span>
            </button>
            <button
            onClick={exportToExcel}
            className="flex items-center gap-2 bg-white border px-4 py-2 rounded-md text-sm hover:bg-gray-50 transition-all"
            >
            <FiDownload size={16} />
            <span>Export</span>
            </button>
        </div>
      </div>

      {/* Controls Section */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <input
          type="text"
          placeholder="Search by title, location, or category..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setCurrentPage(1);
          }}
          className="border rounded-md px-4 py-2 text-sm w-full lg:w-80 outline-none focus:ring-2 ring-gray-200"
        />

        <div className="flex flex-wrap items-center gap-3">
          <select
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="border rounded-md px-4 py-2 text-sm outline-none"
          >
            <option>All Categories</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>

          <div className="text-sm text-gray-500">
            Showing {filteredData.length} entries
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="overflow-x-auto scrollbar-hide border rounded-lg bg-white">
        <div className="min-w-[900px]">
          <div className="grid grid-cols-[2fr_1fr_1.5fr_1fr_80px] bg-gray-100 px-6 py-4 text-sm font-semibold text-gray-700">
            <div>Property Title</div>
            <div>Category</div>
            <div>Location</div>
            <div className="text-center">Status</div>
            <div className="text-center">Actions</div>
          </div>

          {currentData.length === 0 ? (
            <div className="px-6 py-10 text-center text-gray-500">
              No properties found
            </div>
          ) : (
            currentData.map((item) => (
              <div
                key={item._id}
                className="grid grid-cols-[2fr_1fr_1.5fr_1fr_80px] items-center px-6 py-4 text-sm border-t hover:bg-[#F7F9F4] transition-colors"
              >
                <div className="font-medium text-gray-800 line-clamp-1">{item.title}</div>
                <div>{item.category || "-"}</div>
                <div className="text-gray-500 italic line-clamp-1">{item.location}</div>
                <div className="flex justify-center">
                  <span className={`px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ${
                    item.status === "Available" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"
                  }`}>
                    {item.status || "Draft"}
                  </span>
                </div>

                <div className="flex justify-center">
                  <button
                    ref={(el) => (btnRefs.current[item._id] = el)}
                    onClick={() => handleToggle(item._id)}
                    className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                  >
                    <FiMoreHorizontal size={18} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Action Dropdown */}
        {openMenuId && (
          <div
            ref={menuRef}
            className="fixed w-36 bg-white border rounded-lg shadow-xl text-sm z-[9999] overflow-hidden"
            style={{ top: menuPosition.top, left: menuPosition.left }}
          >
            <button
              onClick={() => navigate(`/admin-panel/property-list/property-form?mode=view&id=${openMenuId}`)}
              className="flex items-center gap-2 w-full px-4 py-3 hover:bg-gray-100 text-gray-700"
            >
              <FiEye className="text-blue-500" /> View
            </button>
            <button
              onClick={() => navigate(`/admin-panel/property-list/property-form?mode=edit&id=${openMenuId}`)}
              className="flex items-center gap-2 w-full px-4 py-3 hover:bg-gray-100 text-gray-700 border-t"
            >
              <FiEdit className="text-orange-500" /> Edit
            </button>
            <button
              onClick={() => { setSelectedId(openMenuId); setShowDeleteModal(true); setOpenMenuId(null); }}
              className="flex items-center gap-2 w-full px-4 py-3 hover:bg-red-50 text-red-600 border-t"
            >
              <FiTrash2 /> Delete
            </button>
          </div>
        )}

        {/* Pagination Footer */}
        {filteredData.length > 0 && (
          <div className="flex items-center justify-between bg-gray-50 px-6 py-4 border-t">
            <div className="text-sm text-gray-600 font-medium">
              Page {currentPage} of {totalPages}
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                className="px-4 py-1.5 border rounded-md bg-white text-sm font-medium disabled:opacity-40 hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-4 py-1.5 border rounded-md bg-[#45A301] text-white text-sm font-medium disabled:opacity-50 hover:bg-[#3a8a01] transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[10000] p-4">
          <div className="bg-white w-full max-w-sm rounded-xl shadow-2xl overflow-hidden">
            <div className="flex justify-between items-center border-b px-6 py-4 bg-gray-50">
              <h3 className="font-bold text-gray-800">Delete Property</h3>
              <button onClick={() => setShowDeleteModal(false)} className="p-1 hover:bg-gray-200 rounded-full">
                <FiX size={20} />
              </button>
            </div>
            <div className="px-6 py-6 text-sm text-gray-600">
              Are you sure you want to delete this property? This action cannot be undone.
            </div>
            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
              <button 
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 border rounded-md text-sm font-medium hover:bg-white"
              >
                Cancel
              </button>
              <button 
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700"
              >
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyList;